import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import PermissionDeniedCard from '../components/PermissionDeniedCard';
import usePermissions from '../hooks/usePermissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const QUOTE_SITUATION_PERMISSION = 'ADMIN_SITUAZIONE_QUOTE';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('it-IT');
};

const formatAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '-';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(numeric);
};

const normalizeSearchValue = (value) => String(value ?? '').toLowerCase().trim();

const quoteRowToSearchableText = (row) => [
  row.user_name,
  row.group_name,
  row.pagato ? 'pagato' : 'non pagato',
  formatDate(row.data_inizio),
  formatDate(row.data_fine),
  row.data_inizio,
  row.data_fine,
  formatAmount(row.prezzo),
  row.prezzo
].join(' ');

const quotePerUserToSearchableText = (row) => [
  row.first_name,
  row.last_name,
  row.email,
  row.group_name,
  row.stato_pagamento ? 'pagato' : 'non pagato',
  formatDate(row.ultima_data_fine_pagata),
  row.ultima_data_fine_pagata,
  formatAmount(row.ultimo_importo_pagato),
  row.ultimo_importo_pagato
].join(' ');

const filterRowsBySearchTerm = (rows, searchTerm, toSearchableText) => {
  if (!searchTerm) return rows;
  return rows.filter((row) => normalizeSearchValue(toSearchableText(row)).includes(searchTerm));
};

const StatusBadge = ({ paid }) => (
  <span className={`px-2 py-1 text-xs rounded ${paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
    {paid ? 'Pagato' : 'Non pagato'}
  </span>
);

const QuoteTable = ({ rows }) => {
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-gray-500">Nessun risultato</p>;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-3 sm:hidden">
        {rows.map((row) => (
          <div key={row.id} className="rounded-lg border p-3">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div>
                <p className="font-medium">{row.user_name || '-'}</p>
                <p className="text-xs text-gray-500">{row.group_name || '-'}</p>
              </div>
              <StatusBadge paid={!!row.pagato} />
            </div>
            <div className="space-y-1 text-sm">
              <p><span className="font-medium">Data inizio:</span> {formatDate(row.data_inizio)}</p>
              <p><span className="font-medium">Data fine:</span> {formatDate(row.data_fine)}</p>
              <p><span className="font-medium">Importo:</span> {formatAmount(row.prezzo)}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto sm:block">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-3 font-medium">Utente</th>
              <th className="py-2 pr-3 font-medium">Gruppo</th>
              <th className="py-2 pr-3 font-medium">Data inizio</th>
              <th className="py-2 pr-3 font-medium">Data fine</th>
              <th className="py-2 pr-3 font-medium">Stato pagamento</th>
              <th className="py-2 pr-3 font-medium">Importo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="border-b last:border-b-0">
                <td className="py-3 pr-3">
                  <span className="font-medium">{row.user_name || '-'}</span>
                </td>
                <td className="py-3 pr-3">{row.group_name || '-'}</td>
                <td className="py-3 pr-3">{formatDate(row.data_inizio)}</td>
                <td className="py-3 pr-3">{formatDate(row.data_fine)}</td>
                <td className="py-3 pr-3"><StatusBadge paid={!!row.pagato} /></td>
                <td className="py-3 pr-3 font-medium">{formatAmount(row.prezzo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function QuoteSituationPage() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState({
    quote_per_utente: [],
    quote_pagate: [],
    quote_non_pagate: []
  });

  const canView = hasPermission(QUOTE_SITUATION_PERMISSION);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/admin/quote-situation');
        setData({
          quote_per_utente: response.data?.quote_per_utente || [],
          quote_pagate: response.data?.quote_pagate || [],
          quote_non_pagate: response.data?.quote_non_pagate || []
        });
      } catch (error) {
        console.error('Failed to fetch quote situation', error);
        setData({
          quote_per_utente: [],
          quote_pagate: [],
          quote_non_pagate: []
        });
      } finally {
        setLoading(false);
      }
    };

    if (permissionsLoading) {
      return;
    }

    if (!canView) {
      setLoading(false);
      return;
    }

    fetchData();
  }, [permissionsLoading, canView]);

  const quotePerUtente = useMemo(() => data.quote_per_utente || [], [data.quote_per_utente]);
  const quotePagate = useMemo(() => data.quote_pagate || [], [data.quote_pagate]);
  const quoteNonPagate = useMemo(() => data.quote_non_pagate || [], [data.quote_non_pagate]);
  const normalizedSearchTerm = useMemo(() => normalizeSearchValue(searchTerm), [searchTerm]);

  const filteredQuotePerUtente = useMemo(
    () => filterRowsBySearchTerm(quotePerUtente, normalizedSearchTerm, quotePerUserToSearchableText),
    [quotePerUtente, normalizedSearchTerm]
  );
  const filteredQuotePagate = useMemo(
    () => filterRowsBySearchTerm(quotePagate, normalizedSearchTerm, quoteRowToSearchableText),
    [quotePagate, normalizedSearchTerm]
  );
  const filteredQuoteNonPagate = useMemo(
    () => filterRowsBySearchTerm(quoteNonPagate, normalizedSearchTerm, quoteRowToSearchableText),
    [quoteNonPagate, normalizedSearchTerm]
  );

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="mx-auto max-w-7xl px-4 pb-6 pt-20 sm:px-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold sm:text-3xl">Situazione quote</h1>
          <p className="text-gray-600">Panoramica amministrativa delle quote associative</p>
        </div>

        {permissionsLoading || loading ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-600">
              Caricamento...
            </CardContent>
          </Card>
        ) : !canView ? (
          <div className="flex justify-center">
            <PermissionDeniedCard message="Permesso ADMIN_SITUAZIONE_QUOTE richiesto" />
          </div>
        ) : (
          <Tabs defaultValue="per-utente" className="w-full">
            <div className="mb-4">
              <Input
                placeholder="Filtra righe per utente, email, gruppo, stato, data o importo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:max-w-md"
              />
            </div>

            <TabsList className="grid h-auto w-full grid-cols-1 gap-1 md:grid-cols-3">
              <TabsTrigger value="per-utente" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
                Quote per utente
              </TabsTrigger>
              <TabsTrigger value="pagate" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
                Lista quote pagate
              </TabsTrigger>
              <TabsTrigger value="non-pagate" className="h-auto min-h-10 whitespace-normal px-2 py-2 text-center text-xs leading-tight sm:text-sm">
                Lista quote non pagate
              </TabsTrigger>
            </TabsList>

            <TabsContent value="per-utente">
              <Card>
                <CardHeader>
                  <CardTitle>Quote per utente</CardTitle>
                  <CardDescription>Stato pagamento per ciascun utente</CardDescription>
                </CardHeader>
                <CardContent>
                  {filteredQuotePerUtente.length === 0 ? (
                    <p className="py-6 text-center text-sm text-gray-500">Nessun risultato</p>
                  ) : (
                    <div className="space-y-3">
                      <div className="space-y-3 sm:hidden">
                        {filteredQuotePerUtente.map((row) => (
                          <div key={row.user_id} className="rounded-lg border p-3">
                            <div className="mb-2 flex items-start justify-between gap-3">
                              <div>
                                <p className="font-medium">{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}</p>
                                <p className="text-xs text-gray-500">{row.email || '-'}</p>
                              </div>
                              <StatusBadge paid={!!row.stato_pagamento} />
                            </div>
                            <div className="space-y-1 text-sm">
                              <p><span className="font-medium">Ultima scadenza pagata:</span> {row.stato_pagamento ? formatDate(row.ultima_data_fine_pagata) : '-'}</p>
                              <p><span className="font-medium">Ultimo importo pagato:</span> {row.stato_pagamento ? formatAmount(row.ultimo_importo_pagato) : '-'}</p>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="hidden overflow-x-auto sm:block">
                        <table className="w-full min-w-[700px] text-sm">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="py-2 pr-3 font-medium">Utente</th>
                              <th className="py-2 pr-3 font-medium">Stato pagamento</th>
                              <th className="py-2 pr-3 font-medium">Data fine ultimo pagamento</th>
                              <th className="py-2 pr-3 font-medium">Importo ultimo pagamento</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredQuotePerUtente.map((row) => (
                              <tr key={row.user_id} className="border-b last:border-b-0">
                                <td className="py-3 pr-3">
                                  <div className="font-medium">{`${row.first_name || ''} ${row.last_name || ''}`.trim() || '-'}</div>
                                  <div className="text-xs text-gray-500">{row.email || '-'}</div>
                                </td>
                                <td className="py-3 pr-3"><StatusBadge paid={!!row.stato_pagamento} /></td>
                                <td className="py-3 pr-3">{row.stato_pagamento ? formatDate(row.ultima_data_fine_pagata) : '-'}</td>
                                <td className="py-3 pr-3 font-medium">{row.stato_pagamento ? formatAmount(row.ultimo_importo_pagato) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="pagate">
              <Card>
                <CardHeader>
                  <CardTitle>Lista quote pagate</CardTitle>
                  <CardDescription>Quote con stato pagamento completato</CardDescription>
                </CardHeader>
                <CardContent>
                  <QuoteTable rows={filteredQuotePagate} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="non-pagate">
              <Card>
                <CardHeader>
                  <CardTitle>Lista quote non pagate</CardTitle>
                  <CardDescription>Quote ancora da saldare</CardDescription>
                </CardHeader>
                <CardContent>
                  <QuoteTable rows={filteredQuoteNonPagate} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
